import { useState, type FC } from "react";
import TextButton from "../../atoms/buttons/TextButton";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import Subtitle from "../../atoms/typography/Subtitle";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { AnimatePresence, motion } from "framer-motion";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Subheading from "../../atoms/typography/Subheading";
import BodySmall from "../../atoms/typography/BodySmall";
import RelationshipInvoiceCharts from "./RelationshipInvoiceCharts";
import { useTranslation } from "react-i18next";
import { AgreementIcon } from "../../atoms/icons/AgreementIcon";
import { useClientRelationship } from "../../../graphql/hooks/client/useClientRelationship";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import { useNavigate } from "react-router-dom";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import { OpenLinkIconSmall } from "../../atoms/icons/OpenLinkIcon";
import { CalendarIcon } from "../../atoms/icons/CalendarIcon";
import dayjs from "dayjs";
import Alert from "../../molecules/Alert";
import { FileIconOne } from "../../atoms/icons/FileIcons";
import { RecieptIcon } from "../../atoms/icons/ReceiptIcons";

interface Props {
  issuerClientIdentifier: string;
  recipientClientIdentifier: string;
  issuerName?: string;
  recipientName?: string;
}

const OverlayContent = styled(motion.div)`
  padding: 24px;
  background-color: white;
  border-radius: 4px;
  max-height: 90vh;
  width: 80vw;
  position: fixed;
  top: 0;
  right: 0;
  box-shadow: ${theme.shadow.cardShadow};
  z-index: 100;
  overflow-y: scroll;
`;

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

const FlexedDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border-radius: 8px;
  background-color: transparent;
  border: 1px solid ${theme.colors.info20};
`;

export const RelationshipOverlay: FC<Props> = ({
  issuerClientIdentifier,
  recipientClientIdentifier,
  issuerName,
  recipientName,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  const { clientRelationship, error, loading } = useClientRelationship(
    open,
    issuerClientIdentifier,
    recipientClientIdentifier,
  );

  return (
    <>
      <TextButton
        color={theme.colors.primary}
        title={t("invoiceAssessment.relationship")}
        icon={<AgreementIcon color={theme.colors.primary} />}
        onClick={() => setOpen(true)}
      />

      <AnimatePresence>
        {open && (
          <>
            <Backdrop
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            <OverlayContent
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Row nogutter align="center" justify="between">
                <Subtitle color={theme.colors.primary}>
                  {t("invoiceAssessment.relationship")}
                </Subtitle>
                <IconButton
                  icon={<CrossIcon />}
                  color={theme.colors.white}
                  onClick={() => setOpen(false)}
                />
              </Row>
              <HorizontalSpacer spacing={1} />

              <Row nogutter align="center" style={{ gap: 16 }}>
                <TextLinkButton
                  title={issuerName ?? issuerClientIdentifier}
                  onClick={() => navigate(`/client/${issuerClientIdentifier}`)}
                  icon={<OpenLinkIconSmall />}
                />
                <BodySmall fontweight="700">&</BodySmall>
                <TextLinkButton
                  title={recipientName ?? recipientClientIdentifier}
                  onClick={() =>
                    navigate(`/client/${recipientClientIdentifier}`)
                  }
                  icon={<OpenLinkIconSmall />}
                />
              </Row>
              {loading && <SmallLoadingSpinner />}
              {error && (
                <Alert
                  type="error"
                  title="Kunde inte hämta relationsstatistiken"
                  message={error.message}
                />
              )}

              <HorizontalSpacer spacing={1} />
              <Row align="center">
                <Col lg={3}>
                  <StatContainer>
                    <FlexedDiv>
                      <AgreementIcon />
                      <Subheading>
                        {t("invoiceAssessment.contactSince")}{" "}
                      </Subheading>
                    </FlexedDiv>
                    <BodySmall>
                      {clientRelationship?.firstInvoiceDate
                        ? dayjs(clientRelationship?.firstInvoiceDate).format(
                            "YYYY-MM-DD",
                          )
                        : "-"}
                    </BodySmall>
                  </StatContainer>
                </Col>
                <Col lg={3}>
                  <StatContainer>
                    <FlexedDiv>
                      <CalendarIcon />
                      <Subheading>
                        {t("invoiceAssessment.averageDaysToPayment")}
                      </Subheading>
                    </FlexedDiv>
                    <BodySmall>
                      {clientRelationship?.averageDaysToPayment ?? "-"}
                    </BodySmall>
                  </StatContainer>
                </Col>
                <Col lg={3}>
                  <StatContainer>
                    <FlexedDiv>
                      <FileIconOne />
                      <Subheading>
                        {t("invoiceAssessment.invoiceCount")}
                      </Subheading>
                    </FlexedDiv>
                    <BodySmall>
                      {clientRelationship?.invoiceCount ?? "-"}
                    </BodySmall>
                  </StatContainer>
                </Col>
                <Col lg={3}>
                  <StatContainer>
                    <FlexedDiv>
                      <RecieptIcon />
                      <Subheading>
                        {t("invoiceAssessment.paidInvoiceCount")}
                      </Subheading>
                    </FlexedDiv>
                    <BodySmall>
                      {clientRelationship?.paidInvoiceCount ?? "-"}
                    </BodySmall>
                  </StatContainer>
                </Col>
              </Row>
              <HorizontalSpacer spacing={1} />
              <RelationshipInvoiceCharts
                doneInvoices={clientRelationship?.doneInvoices}
                openInvoices={clientRelationship?.openInvoices}
              />

              <HorizontalSpacer spacing={2} />
              {/*<OutlinedContainer>
                <IntercompanyLedgerList invoices={exampleOpenInvoices} />
              </OutlinedContainer>*/}
            </OverlayContent>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
export default RelationshipOverlay;
